import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { NOPING_GREEN, SHIELD_BLUE, SHIELD_CYAN } from '../constants';

const BRIDGE_DATA = [
  { cy: 28, color: NOPING_GREEN, ping: '2ms' },
  { cy: 53, color: SHIELD_BLUE, ping: '8ms' },
  { cy: 78, color: SHIELD_CYAN, ping: '12ms' },
  { cy: 103, color: '#66BB6A', ping: '18ms' },
  { cy: 128, color: '#FFA726', ping: '24ms' },
];

function PacketDot({ path, color, dur, begin, r = 2.5 }: { path: string; color: string; dur: string; begin: string; r?: number }) {
  return (
    <>
      <circle r={r} fill={color} opacity='0.9'>
        <animateMotion dur={dur} begin={begin} repeatCount='indefinite' path={path} />
      </circle>
      <circle r={r * 1.8} fill={color} opacity='0.12'>
        <animateMotion dur={dur} begin={begin} repeatCount='indefinite' path={path} />
      </circle>
    </>
  );
}

export function SwordDiagram() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });

  const playerX = 30;
  const playerY = 80;
  const bridgeX = 170;
  const selectorX = 290;
  const serverX = 375;

  return (
    <div ref={ref} className='relative h-44 overflow-hidden' style={{ background: `linear-gradient(180deg, ${NOPING_GREEN}06 0%, transparent 100%)` }}>
      <svg viewBox='0 0 420 170' className='size-full' preserveAspectRatio='xMidYMid meet'>
        <defs>
          <filter id='v5a-sw-glow'>
            <feGaussianBlur stdDeviation='2.5' result='b' />
            <feMerge>
              <feMergeNode in='b' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
          <filter id='v5a-sw-intense'>
            <feGaussianBlur stdDeviation='4' result='b' />
            <feMerge>
              <feMergeNode in='b' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
          <linearGradient id='v5a-sw-out' x1='0' y1='0' x2='1' y2='0'>
            <stop offset='0%' stopColor={SHIELD_CYAN} />
            <stop offset='100%' stopColor={NOPING_GREEN} />
          </linearGradient>
        </defs>

        {[30, 60, 100, 140, 170].map((y, i) => (
          <line key={`sl-${i}`} x1='0' y1={y} x2='420' y2={y} stroke='#fff' strokeWidth='0.3' opacity='0.03' />
        ))}

        {Array.from({ length: 8 }).map((_r, row) => Array.from({ length: 16 }).map((_c, col) => <circle key={`bg-${row}-${col}`} cx={20 + col * 25} cy={12 + row * 25} r='0.6' fill='#fff' opacity='0.03' />))}

        {BRIDGE_DATA.map((bridge, i) => {
          const cpY = playerY + (bridge.cy - playerY) * 0.4;
          const path = `M${playerX + 18},${playerY} C${playerX + 55},${cpY} ${bridgeX - 40},${bridge.cy} ${bridgeX - 18},${bridge.cy}`;
          const isOptimal = i === 0;
          return <motion.path key={`fan-${i}`} d={path} fill='none' stroke={bridge.color} strokeWidth={isOptimal ? 2.2 : 1.2} strokeOpacity={isOptimal ? 0.5 : 0.25} initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : {}} transition={{ duration: 0.7, delay: 0.2 + i * 0.06 }} />;
        })}

        {BRIDGE_DATA.map((bridge, i) => {
          const cpY = bridge.cy + (playerY - bridge.cy) * 0.6;
          const path = `M${bridgeX + 18},${bridge.cy} C${bridgeX + 40},${bridge.cy} ${selectorX - 40},${cpY} ${selectorX - 16},${playerY}`;
          const isOptimal = i === 0;
          return <motion.path key={`conv-${i}`} d={path} fill='none' stroke={bridge.color} strokeWidth={isOptimal ? 2 : 1} strokeOpacity={isOptimal ? 0.4 : 0.2} initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : {}} transition={{ duration: 0.6, delay: 0.5 + i * 0.06 }} />;
        })}

        {BRIDGE_DATA.map((bridge, i) => {
          const isOptimal = i === 0;
          return (
            <motion.g key={`bridge-${i}`} initial={{ scale: 0, opacity: 0 }} animate={inView ? { scale: 1, opacity: 1 } : {}} transition={{ type: 'spring', delay: 0.35 + i * 0.06, stiffness: 200 }}>
              {isOptimal && <motion.circle cx={bridgeX} cy={bridge.cy} r='18' fill='none' stroke={bridge.color} strokeWidth='0.6' animate={{ r: [18, 22, 18], opacity: [0.3, 0.08, 0.3] }} transition={{ duration: 2.5, repeat: Infinity }} />}
              <rect x={bridgeX - 14} y={bridge.cy - 10} width='28' height='20' rx='4' fill={isOptimal ? `${bridge.color}15` : `${bridge.color}08`} stroke={bridge.color} strokeWidth={isOptimal ? 1.5 : 0.8} filter={isOptimal ? 'url(#v5a-sw-glow)' : undefined} />
              <text x={bridgeX} y={bridge.cy + 1} textAnchor='middle' fill={bridge.color} fontSize='6' fontWeight='bold' fontFamily='var(--font-outfit)'>
                B{i + 1}
              </text>
              <text x={bridgeX} y={bridge.cy + 8} textAnchor='middle' fill={bridge.color} fontSize='5' fontFamily='var(--font-outfit)' opacity='0.6'>
                {bridge.ping}
              </text>
            </motion.g>
          );
        })}

        {BRIDGE_DATA.map((bridge, i) => {
          const cpY = playerY + (bridge.cy - playerY) * 0.4;
          const path = `M${playerX + 18},${playerY} C${playerX + 55},${cpY} ${bridgeX - 40},${bridge.cy} ${bridgeX - 18},${bridge.cy}`;
          return (
            <g key={`pkt-l-${i}`}>
              <PacketDot path={path} color={bridge.color} dur={`${1.4 + i * 0.1}s`} begin={`${i * 0.2}s`} r={i === 0 ? 3 : 2} />
              <PacketDot path={path} color={bridge.color} dur={`${1.4 + i * 0.1}s`} begin={`${i * 0.2 + 0.7}s`} r={1.5} />
            </g>
          );
        })}

        {BRIDGE_DATA.map((bridge, i) => {
          const cpY = bridge.cy + (playerY - bridge.cy) * 0.6;
          const path = `M${bridgeX + 18},${bridge.cy} C${bridgeX + 40},${bridge.cy} ${selectorX - 40},${cpY} ${selectorX - 16},${playerY}`;
          return (
            <g key={`pkt-r-${i}`}>
              <PacketDot path={path} color={bridge.color} dur={`${1.3 + i * 0.1}s`} begin={`${i * 0.15 + 0.3}s`} r={i === 0 ? 2.5 : 1.8} />
            </g>
          );
        })}

        <motion.g initial={{ scale: 0, opacity: 0 }} animate={inView ? { scale: 1, opacity: 1 } : {}} transition={{ type: 'spring', delay: 0.1, stiffness: 200 }}>
          <circle cx={playerX} cy={playerY} r='16' fill='#0a1a0f' stroke={NOPING_GREEN} strokeWidth='1.5' />
          <circle cx={playerX} cy={playerY - 4} r='4.5' fill='none' stroke={NOPING_GREEN} strokeWidth='1.3' />
          <path d={`M${playerX - 6},${playerY + 10} Q${playerX},${playerY + 3} ${playerX + 6},${playerY + 10}`} fill='none' stroke={NOPING_GREEN} strokeWidth='1.3' strokeLinecap='round' />
          <text x={playerX} y={playerY + 28} textAnchor='middle' fill={NOPING_GREEN} fontSize='7' fontFamily='var(--font-outfit)' fontWeight='600' opacity='0.8'>
            Player
          </text>
        </motion.g>

        <motion.g initial={{ scale: 0, opacity: 0 }} animate={inView ? { scale: 1, opacity: 1 } : {}} transition={{ type: 'spring', delay: 0.7, stiffness: 150 }}>
          <motion.circle cx={selectorX} cy={playerY} r='20' fill='none' stroke={SHIELD_CYAN} strokeWidth='0.8' animate={{ r: [20, 24, 20], opacity: [0.3, 0.08, 0.3] }} transition={{ duration: 2.5, repeat: Infinity }} />
          <path d={`M${selectorX},${playerY - 16} L${selectorX + 16},${playerY} L${selectorX},${playerY + 16} L${selectorX - 16},${playerY} Z`} fill='#0a1a1f' stroke={SHIELD_CYAN} strokeWidth='1.5' filter='url(#v5a-sw-glow)' />
          <text x={selectorX} y={playerY + 1} textAnchor='middle' fill={SHIELD_CYAN} fontSize='8' fontWeight='900' fontFamily='var(--font-bai)'>
            AI
          </text>
          <text x={selectorX} y={playerY + 28} textAnchor='middle' fill={SHIELD_CYAN} fontSize='6' fontFamily='var(--font-outfit)' fontWeight='600' opacity='0.7'>
            Best Route
          </text>
        </motion.g>

        <motion.path d={`M${selectorX + 16},${playerY} C${selectorX + 35},${playerY} ${serverX - 35},${playerY} ${serverX - 18},${playerY}`} fill='none' stroke='url(#v5a-sw-out)' strokeWidth='3' strokeOpacity='0.6' filter='url(#v5a-sw-intense)' initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : {}} transition={{ duration: 0.5, delay: 0.9 }} />
        <PacketDot path={`M${selectorX + 16},${playerY} L${serverX - 18},${playerY}`} color={NOPING_GREEN} dur='0.7s' begin='0s' r={3.5} />
        <PacketDot path={`M${selectorX + 16},${playerY} L${serverX - 18},${playerY}`} color={SHIELD_CYAN} dur='0.7s' begin='0.35s' r={2.5} />
        <PacketDot path={`M${selectorX + 16},${playerY} L${serverX - 18},${playerY}`} color={NOPING_GREEN} dur='0.8s' begin='0.6s' r={2} />

        <motion.g initial={{ scale: 0, opacity: 0 }} animate={inView ? { scale: 1, opacity: 1 } : {}} transition={{ type: 'spring', delay: 1 }}>
          <circle cx={serverX} cy={playerY} r='18' fill='#0a1a0f' stroke={NOPING_GREEN} strokeWidth='1.5' filter='url(#v5a-sw-glow)' />
          <rect x={serverX - 7} y={playerY - 9} width='14' height='5' rx='1.5' fill='none' stroke={NOPING_GREEN} strokeWidth='1' />
          <rect x={serverX - 7} y={playerY - 2} width='14' height='5' rx='1.5' fill='none' stroke={NOPING_GREEN} strokeWidth='1' />
          <motion.circle cx={serverX + 4} cy={playerY - 6.5} r='1.2' fill={NOPING_GREEN} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1, repeat: Infinity }} />
          <circle cx={serverX + 4} cy={playerY + 0.5} r='1.2' fill={NOPING_GREEN} opacity='0.6' />
          <text x={serverX} y={playerY + 28} textAnchor='middle' fill={NOPING_GREEN} fontSize='7' fontFamily='var(--font-outfit)' fontWeight='600' opacity='0.8'>
            Game Server
          </text>
        </motion.g>

        <motion.text x={210} y={12} textAnchor='middle' fill={NOPING_GREEN} fontSize='8' fontWeight='bold' fontFamily='var(--font-bai)' initial={{ opacity: 0 }} animate={inView ? { opacity: 0.8 } : {}} transition={{ delay: 0.3 }}>
          5 Bridges x 5 Hops
        </motion.text>

        <motion.g initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 1.1 }}>
          <rect x={bridgeX - 65} y={145} width={130} height={20} rx={10} fill={`${NOPING_GREEN}12`} stroke={NOPING_GREEN} strokeWidth='0.8' />
          <text x={bridgeX} y={158} textAnchor='middle' fill={NOPING_GREEN} fontSize='8' fontWeight='bold' fontFamily='var(--font-outfit)'>
            <animate attributeName='opacity' values='0.6;1;0.6' dur='2s' repeatCount='indefinite' />
            ~32 QUADRILLION ROUTES
          </text>
        </motion.g>
      </svg>
    </div>
  );
}
